import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import projectService from '../services/projectService'
import { useAuth } from './useAuth'

/**
 * Custom hook for fetching user's own projects with status filtering
 * @param {string|null} statusFilter - Filter by status ('active', 'completed', null for all)
 * @returns {object} Query result with filtered projects
 */
export const useUserProjects = (statusFilter = null) => {
  const { user, isAuthenticated } = useAuth()
  
  const queryKey = ['userProjects', user?.id, statusFilter]
  
  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
    status
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await projectService.getUserProjectsForProjectsPage(statusFilter)
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch user projects')
      }
      
      return result
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 5 * 60 * 1000, // 5 minutes cache time
  })

  // Filter projects by status on the client side
  const projects = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return []
    
    const allProjects = data.data
    
    if (!statusFilter || statusFilter === 'all') {
      return allProjects
    }
    
    return allProjects.filter(project => project.status === statusFilter)
  }, [data, statusFilter])

  // Calculate status counts for UI
  const statusCounts = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return { all: 0, active: 0, completed: 0, draft: 0, submitted: 0 }
    
    const allProjects = data.data
    
    return {
      all: allProjects.length,
      active: allProjects.filter(p => p.status === 'active').length,
      completed: allProjects.filter(p => p.status === 'completed').length,
      draft: allProjects.filter(p => p.status === 'draft').length,
      submitted: allProjects.filter(p => p.status === 'submitted').length
    }
  }, [data])

  return {
    projects,
    statusCounts,
    totalCount: projects.length,
    error,
    isLoading,
    isError,
    refetch,
    status,
    isAuthenticated
  }
}

export default useUserProjects